<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="12">
          <k-card-header :items="headerItems"></k-card-header>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="loading">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && workingEocEmailConfig">

      <!-- Send Email -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h5">Send Email</span>
            </v-card-title>
            <v-card-text>
              <v-container>

                <!-- Cycle -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Cycle</b>
                  </v-col>
                  <v-col md="auto" class="pa-0" v-if="cycleDropDownItems.length > 0">
                    <v-autocomplete
                      outlined dense filled
                      :items="cycleDropDownItems"
                      v-model="selectedCycleDropDownItem"
                      item-text="dropDate"
                      item-value="id"
                      :loading="loading"
                      return-object/>
                  </v-col>
                  <v-col md="auto" v-if="cycleDropDownItems.length == 0">
                    <span>No cycles were found for this FI!</span>
                  </v-col>
                </v-row>

                <!-- Test Email Address -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Test Email Address</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      v-model="testEmailAddress"
                      ref="testEmailAddress">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Send Test Email -->
                <v-row class="ma-0 pa-0">
                  <v-col md="4" offset-md="4" class="pa-0">
                    <v-btn
                      color="primary"
                      ref="testEmailButton"
                      @click="sendTestEmail()"
                      :disabled="!testEmailAddress || !selectedCycleDropDownItem"
                      >Send Test Email
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Enable Send Email -->
                <v-row >
                  <v-col class="align" md="3" offset-md="1">
                    <v-checkbox class="ma-n1" align="end" hide-details dense
                      ref="sendEmailCheckbox" v-model="enableSendEmail">
                      <template v-slot:label>
                        <h5>Enable Send Email</h5>
                      </template>
                    </v-checkbox>
                  </v-col>

                  <v-col md="auto" class="ma-n2 align">
                    <v-btn
                      color="primary"
                      ref="sendEmailButton"
                      @click="sendEmailNow()"
                      :disabled="!selectedCycleDropDownItem || !enableSendEmail"
                      >Send Now
                    </v-btn>
                    <span v-if="workingEocEmailConfig.lastSentDate">
                      &nbsp; (Email last sent on {{workingEocEmailConfig.lastSentDate}})
                    </span>
                    <span v-if="! workingEocEmailConfig.lastSentDate">
                      &nbsp; (Never sent any email before!)
                    </span>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>

      <!-- Email Configuration -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h5">Email Configuration</span>
            </v-card-title>
            <v-card-text>
              <v-container class="ma-0 pa-0">
              <v-form ref="form" v-model="isValid" lazy-validation>

                <!-- FI Name -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Name</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      v-model="workingEocEmailConfig.fiName"
                      ref="fiName"
                      :rules="[() => !!workingEocEmailConfig.fiName || 'FI Name is required!']"
                      required>
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- FI Products -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Products</b>
                  </v-col>
                  <v-col md="auto" class="ma-0 pa-0">

                  <v-data-table dense v-if="!isEmailCountLoading"
                    :items="activeProducts"
                    :footer-props="footerProps"
                    :hide-default-footer="true"
                    id="fiProductTable">
                    <template v-slot:item="{ item }">
                      <tr>
                        <td>
                          <v-checkbox hide-details class="ma-0 pa-0" dense
                              @change="onEocEmailEnableFlagModified()"
                              v-model="item.eocEmailEnabled">
                            </v-checkbox>
                        </td>
                        <td>{{ item.productMapping.fiProductName}}</td>
                        <td class="italic-small-span">
                          <span>{{ getLatestProductSendTimestamp(item) }} </span>
                        </td>
                        <td class="small-span">
                          <span> {{ getEocEmailCount(item) }} </span>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>

                  </v-col>
                </v-row>

                <!-- Bank/Credit Union -->
                <v-row align="center" class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Bank/Credit Union</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-radio-group row v-model="workingEocEmailConfig.bankOrCu"
                        ref="terminalFeeCalculationMethod"
                        :rules="[() => !!workingEocEmailConfig.bankOrCu
                            || 'Bank or Credit Union is required!!']">
                        <v-radio
                          label="Bank"
                          value="BANK"
                          ref="bank"
                        />
                        <v-radio
                          label="Credit Union"
                          value="CREDIT_UNION"
                          ref="credit_union"
                        />
                      </v-radio-group>
                  </v-col>
                </v-row>

                <!-- Account-Holder Nomenclature -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Account-Holder Nomenclature</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled v-model="workingEocEmailConfig.accountholderNomenclature"
                     :rules="[() => !!workingEocEmailConfig.accountholderNomenclature
                     || 'Account-Holder Nomenclature is required!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- FI "From" Email Address -->
                <v-row class="ma-0 pa-0" v-if="workingEocEmailConfig.emailProvider == 'SPARKPOST'">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI "From" Email Address</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fromEmailAddress"
                      id="fromEmailAddress"
                      v-model="workingEocEmailConfig.fromEmailAddress"
                      :rules="fromEmailAddressRule">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- FI Contact Phone Number -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Contact Phone Number</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      v-model="workingEocEmailConfig.fiContactPhone"
                      :rules="[() => !!workingEocEmailConfig.fiContactPhone
                          || 'FI Contact Phone Number is required!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- FI Contact Email -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Contact Email/URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      v-model="workingEocEmailConfig.fiContactEmail"
                      :rules="[() => !!workingEocEmailConfig.fiContactEmail
                          || 'FI Contact Email or URL is required!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- FI Homepage URL -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Homepage URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      @change="onHomeUrlChange()"
                      v-model="workingEocEmailConfig.fiHomePageUrl"
                      :rules="[() => !workingEocEmailConfig.fiHomePageUrl
                          || urlPattern.test(workingEocEmailConfig.fiHomePageUrl)
                          || 'FI Homepage URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row class="ma-0 pa-0">
                  <v-col class="pa-0" md="3" offset-md="1">

                    <v-tooltip bottom v-if="invalidUrlError || (eocEmailConfig && eocEmailConfig.fiHomePageUrlManualVerified)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          ref="verificationOverrideMessageIcon"
                          v-bind="attrs"
                          v-on="on"
                          fixed top right
                          :class="{'ma-0':true, 'pa-0':true, 'mr-2': true, 'red--text': invalidUrlError}"
                          style="float:right">fa-exclamation-circle</v-icon>
                      </template>
                      <span>
                        <div v-if="invalidUrlError">
                          This homepage URL <b>failed verification</b> using server-side checking.  <br/>
                          If you know this URL is valid, you can override the backend check using this checkbox.
                        </div>
                        <div v-else>
                          This homepage URL's server-side validation checks were <b>manually overridden</b>. <br/>
                          You can uncheck this checkbox to reattempt the verification of the URL when trying to save your changes.
                        </div>
                      </span>
                    </v-tooltip>
                  </v-col>
                  <v-col md="4" class="ma-0 pa-0">
                    <v-checkbox
                          :disabled="!invalidUrlError && !(eocEmailConfig && eocEmailConfig.fiHomePageUrlManualVerified)"
                          :class="{'ma-0': true, 'pa-0': true, 'd-inline-block' : true, 'mr-2': true, 'red--text': invalidUrlError}"
                          outlined dense filled top
                          :color="invalidUrlError ? 'red' : '' "
                          v-model="workingEocEmailConfig.fiHomePageUrlManualVerified"
                          label="Manually override homepage URL validation" >
                          <template v-slot:label>
                            <span :class="{'ma-0': true, 'pa-0': true, 'd-inline-block' : true, 'mr-2': true, 'red--text': invalidUrlError}">Manually override homepage URL validation</span>
                          </template>
                    </v-checkbox>
                  </v-col>
                </v-row>

                <!-- FI Logo URL -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>FI Logo URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      v-model="workingEocEmailConfig.logoUrl"
                      :rules="[() => !!workingEocEmailConfig.logoUrl
                          || 'FI Logo URL is required!',
                        () => urlPattern.test(workingEocEmailConfig.logoUrl)
                          || 'FI Logo URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Insurance -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Insurance</b>
                  </v-col>
                  <v-col md="auto" class="pa-0">
                    <v-autocomplete
                      outlined dense filled
                      :items="insuranceLogos"
                      v-model="workingEocEmailConfig.insuranceLogo"
                      item-text="text"
                      item-value="key"
                      :loading="loading"
                      :rules="[() => !!workingEocEmailConfig.insuranceLogo
                          || 'Insurance is required!']" />
                  </v-col>
                </v-row>

                <!--  Housing -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Housing</b>
                  </v-col>
                  <v-col md="auto" class="pa-0">
                    <v-autocomplete
                      outlined dense filled
                      @change="onHousingLogoChange()"
                      :items="housingLogos"
                      v-model="workingEocEmailConfig.housingLogo"
                      item-text="text"
                      item-value="key"
                      :loading="loading"
                      :rules="[() => !!workingEocEmailConfig.housingLogo
                          || 'Housing logo is required!']" />
                  </v-col>
                </v-row>

                <!-- Confidentiality Statement -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Confidentiality Statement</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-checkbox
                      hide-details dense
                      v-model="workingEocEmailConfig.enableConfidentialityStatement"
                      ref="item"
                      @change="isConfigDataModified()"
                      >
                    </v-checkbox>
                  </v-col>
                </v-row>

                <!-- Banner Color Hex Code -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Banner Color Hex Code</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-row class="ma-0 pa-0">
                      <v-text-field outlined dense filled hide-details="auto"
                        v-model="workingEocEmailConfig.bannerColor"
                        ref="bannerColor"
                        :rules="[() => !!workingEocEmailConfig.bannerColor
                            || 'Banner Color Hex Code is required!',
                          () => /^#[0-9a-fA-F]{6}$/.test(workingEocEmailConfig.bannerColor)
                            || 'Banner Color Hex Code should be \'#\' followed by 6 hexidecimal digits!']"
                        required>
                      </v-text-field>
                    </v-row>
                    <v-row  class="ma-0 pa-0">
                      <small>Kasasa Colors:
                        <span style="color:#D41A27"> Red (#D41A27),</span>
                        <span style="color:#666666"> Gray (#666666),</span>
                        <span style="color:#44A2BC"> Aqua (#44A2BC),</span>
                        <span style="color:#97999B"> Medium Gray (#97999B),</span>
                        <span style="color:#94C03D"> Green (#94C03D),</span>
                        <span style="color:#F89C1B"> Yellow (#F89C1B)</span>
                      </small>
                    </v-row>
                  </v-col>
                </v-row>

                <!-- Facebook -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Facebook</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled v-model="workingEocEmailConfig.facebookLink">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Twitter -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Twitter</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled v-model="workingEocEmailConfig.twitterLink">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- LinkedIn -->
                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>LinkedIn</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled v-model="workingEocEmailConfig.linkedinLink">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Email service provider -->
                <v-row align="center" class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Email Provider</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-radio-group row v-model="workingEocEmailConfig.emailProvider"
                        ref="emailProviderGroup"
                        :rules="[() => !!workingEocEmailConfig.emailProvider
                            || 'Email provider selection is required!!']">
                        <v-radio
                          label="SparkPost"
                          value="SPARKPOST"
                          ref="sparkpost"
                          id="sparkpost"
                        />
                        <v-radio
                          label="Salesforce Marketing Cloud"
                          value="SALESFORCE_MARKETING_CLOUD"
                          ref="salesforce_marketing_cloud"
                          id="salesforce_marketing_cloud"
                        />
                      </v-radio-group>
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0 pa-0" v-if="workingEocEmailConfig.emailProvider == 'SALESFORCE_MARKETING_CLOUD'">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Batch Size</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field
                              ref="batchSize"
                              id="batchSize"
                              label="Max number emails to send in each batch"
                              v-model="workingEocEmailConfig.batchSize"
                              type="number"
                              hide-spin-buttons
                              dense
                              :rules="batchSizeRule"
                              :min="1"
                              :max="100"
                            />
                  </v-col>
                </v-row>
                <v-row align="center" class="ma-0 pa-0" v-if="workingEocEmailConfig.emailProvider == 'SALESFORCE_MARKETING_CLOUD'">
                  <v-col class="align" md="3" offset-md="1">
                      <b>"From" Sfmc Email Address:</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-row>
                      <v-col cols="6" style="padding-right:0">
                        <v-text-field
                                  ref="fromSfmcEmailUsername"
                                  id="fromSfmcEmailUsername"
                                  v-model="workingEocEmailConfig.fromSfmcEmailUsername"
                                  :rules="sfmcFromEmailFiSubDomain"
                                  hide-spin-buttons
                                  dense
                                />
                      </v-col>
                      <v-col cols="6" style="padding-left:0">
                        <v-text-field
                                  ref="fromSfmcEmailDomain"
                                  id="fromSfmcEmailDomain"
                                  :disabled="true"
                                  v-model="workingEocEmailConfig.fromSfmcEmailDomain"
                                  hide-spin-buttons
                                  dense
                                />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Account-HQ EOC Emails Enabled</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-checkbox
                      hide-details dense
                      v-model="workingEocEmailConfig.pinPointEnabled"
                      ref="item"
                      >
                    </v-checkbox>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>AHQ Data Flow Enabled</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-checkbox
                      hide-details dense
                      v-model="workingEocEmailConfig.ayrCyrDataFlowEnabled"
                      >
                    </v-checkbox>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Choose Your Rewards &ndash; Full URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiCyrUrl"
                      v-model="workingEocEmailConfig.fiCyrUrl"
                      :rules="[() => !workingEocEmailConfig.fiCyrUrl
                          || urlPattern.test(workingEocEmailConfig.fiCyrUrl)
                          || 'Choose Your Rewards Full URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Choose Your Rewards &ndash; Short URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiShortCyrUrl"
                      v-model="workingEocEmailConfig.fiShortCyrUrl"
                      :rules="[() => !workingEocEmailConfig.fiShortCyrUrl
                          || urlPattern.test(workingEocEmailConfig.fiShortCyrUrl)
                          || 'Choose Your Rewards Short URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Dashboard &ndash; Full URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiDashboardUrl"
                      v-model="workingEocEmailConfig.fiDashboardUrl"
                      :rules="[() => !workingEocEmailConfig.fiDashboardUrl
                          || urlPattern.test(workingEocEmailConfig.fiDashboardUrl)
                          || 'Dashboard Full URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Dashboard &ndash; Short URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiShortDashboardUrl"
                      v-model="workingEocEmailConfig.fiShortDashboardUrl"
                      :rules="[() => !workingEocEmailConfig.fiShortDashboardUrl
                          || urlPattern.test(workingEocEmailConfig.fiShortDashboardUrl)
                          || 'Dashboard Short URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Dashboard &ndash; VASA Full URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiVasaDashboardFullUrl"
                      v-model="workingEocEmailConfig.fiVasaDashboardFullUrl"
                      :rules="[() => !workingEocEmailConfig.fiVasaDashboardFullUrl
                          || urlPattern.test(workingEocEmailConfig.fiVasaDashboardFullUrl)
                          || 'Dashboard Full URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="ma-0 pa-0">
                  <v-col class="align" md="3" offset-md="1">
                      <b>Dashboard &ndash; VASA Short URL</b>
                  </v-col>
                  <v-col md="4" class="pa-0">
                    <v-text-field outlined dense filled
                      ref="fiVasaDashboardShortUrl"
                      v-model="workingEocEmailConfig.fiVasaDashboardShortUrl"
                      :rules="[() => !workingEocEmailConfig.fiVasaDashboardShortUrl
                          || urlPattern.test(workingEocEmailConfig.fiVasaDashboardShortUrl)
                          || 'Dashboard VASA Short URL is not valid!']">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Save button -->
                <v-row class="pa-0 ma-0">
                  <v-spacer></v-spacer>
                  <v-col cols="auto" class="mr-auto">
                    <v-btn
                      color="primary"
                      ref="saveButton"
                      @click="saveEocEmailSettings()"
                      :loading="saving"
                      :disabled="saving || hasInvalidBatchSize || !(hasUnsavedConfigData || hasUnsavedEnableEocEmailFlag)"
                      >Save
                    </v-btn>
                  </v-col>
                </v-row>

              </v-form>
              </v-container>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>

      <!-- Cycle Send History -->
      <v-row>
        <v-col cols="12">
          <v-card class="detailCard">
            <v-card-title class="card-title">
              <span class="text-h5">Cycle Send History</span>
            </v-card-title>
            <v-card-text>
              <v-container>

                <v-data-table dense
                    :items="sendHistoryForSelectedCycle"
                    :headers="sendHistoryHeaders"
                    :loading="loading"
                    :footer-props="footerProps"
                    :hide-default-footer="true"
                    ref="sendHistoryTable">
                    <template v-slot:item="{ item }">
                      <tr>
                        <td>{{ formatTimestamp(item.timestamp)}}</td>
                        <td>{{ item.externalProductMappingId}}</td>
                        <td>{{ item.productMappingFiProductName}}</td>
                      </tr>
                    </template>
                  </v-data-table>


              </v-container>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>

    </v-container>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Provide, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import {
  Alert, NoticeClass, Dialog, AuthStore,
} from '@kasasa/fbase-components/lib';
import { KCardHeader } from '@kasasa/fbase-components';
import {
  EocEmailConfig, EocEmailConfigResource, EocEmailEvent,
  EocEmailEventResource, EocEmailMetrics, EocEmailMetricsResource,
  TestEmail, SendEocEmailResource,
} from '@/api/eocemailsettings';
import { Cycle, CycleResource } from '@/api/cycles';
import { Product, ProductResource } from '@/api/product';
import { EmailValidationTool, ValidationTool } from '@/util';

@Component({
  computed: mapState(['fiId', 'configVersion', 'auth']),
  components: {
    KCardHeader,
  },
})
export default class EocEmailSettings extends Vue {
  @Inject('EocEmailConfigResource')
  eocEmailConfigResource!: EocEmailConfigResource;

  @Inject('EocEmailEventResource')
  eocEmailEventResource!: EocEmailEventResource;

  @Inject('CycleResource')
  cycleResource!: CycleResource;

  @Inject('EocEmailMetricsResource')
  eocEmailMetricsResource!: EocEmailMetricsResource;

  @Inject('SendEocEmailResource')
  sendEocEmailResource!: SendEocEmailResource;

  @Inject('ProductResource')
  productResource!: ProductResource;

  fiId!: string;

  auth!: AuthStore;

  eocEmailConfig: EocEmailConfig | null = null;

  workingEocEmailConfig: EocEmailConfig = EocEmailConfigResource.createEmptyEocEmailConfig();

  eocEmailEvents: EocEmailEvent[] = [];

  sendHistoryForSelectedCycle: EocEmailEvent[] = [];

  eocEmailMetricsForSelectedCycle: EocEmailMetrics | null = null;

  activeProducts: Product[] = [];

  cycleDropDownItems: CycleDropDownItem[] = [];

  selectedCycleDropDownItem: CycleDropDownItem|null = null;

  testEmailAddress: string | null = null;

  eocEmailEnableFlagsOnLoad = new Map<number, boolean>();

  // progress status
  loading: boolean = false;

  saving: boolean = false;

  isEmailCountLoading: boolean = false;

  isValid: boolean = true;

  // button enable/disable
  isTestEmailValid: boolean = false;

  enableSendEmail: boolean = false;

  hasUnsavedConfigData: boolean = false;

  hasUnsavedEnableEocEmailFlag: boolean = false;

  hasInvalidBatchSize: boolean = false;

  invalidUrlError: boolean = false;

  batchSizeRule:Array<any> = [(v:string|number) => ValidationTool.requiredAndBetweenRuleFunction(v, 1, 100, 'Batch Size')];

  fromEmailAddressRule:Array<any> = [(v:string) => EmailValidationTool.fromEmailValidation(this.workingEocEmailConfig.fromEmailAddress,
    this.workingEocEmailConfig.emailProvider)];

  sfmcFromEmailFiSubDomain:Array<any> = [(v:string) => EmailValidationTool.fromSfmcEmailValidation(this.workingEocEmailConfig.fromSfmcEmailUsername,
    this.workingEocEmailConfig.emailProvider)];

  insuranceLogos:{ key: string|null, text: string }[]
    = [{ key: null, text: 'Select ...' },
      { key: 'FDIC', text: 'FDIC' },
      { key: 'FDIC_DIF', text: 'FDIC-DIF' },
      { key: 'NCUA', text: 'NCUA' },
      { key: 'NCUA_MSIC', text: 'NCUA-MSIC' },
      { key: 'ASI', text: 'ASI' },
      { key: 'ASI_ESI', text: 'ASI-ESI' },
    ];

  housingLogos:{ key: string|null, text: string }[]
    = [{ key: null, text: 'Select ...' },
      { key: 'EHL', text: 'Equal Housing Lender' },
      { key: 'EHO', text: 'Equal Housing Opportunity' },
      { key: 'NONE', text: 'None' },
    ];

  emailProviders :{ key: string|null, text: string }[]
    = [{ key: null, text: 'Select ...' },
      { key: 'SPARKPOST', text: 'SparkPost' },
      { key: 'SALESFORCE_MARKETING_CLOUD', text: 'Salesforce Marketing Cloud' },
    ];

  sendHistoryHeaders:DataTableHeader[] = [
    { text: 'Timestamp', value: 'timestamp', sortable: false },
    { text: 'Product Mapping ID', value: 'externalProductMappingId', sortable: false },
    { text: 'FI Product Name', value: 'productMappingFiProductName', sortable: false },
  ];

  footerProps = {
    'items-per-page-options': [-1, 25, 50, 100],
  }

  urlPattern = new RegExp('^(https?:\\/\\/)?' // protocol
    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ipv4 address
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

  emailPattern = /.+@.+\..+/;

  headerItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: false,
      link: false,
    },
    {
      text: 'End of Cycle Email',
      disabled: true,
    },
  ];


  @Watch('fiId')
  mounted() {
    this.loadAllData();
  }

  loadAllData() {
    this.eocEmailConfig = null;
    this.loading = true;
    this.eocEmailConfigResource
      .get(this.fiId)
      .then((res: EocEmailConfig) => {
        this.eocEmailConfig = res;
        this.initializeWorkingEocEmailConfig();
        this.getCycles();
      })
      .catch((error) => {
        this.showError(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  initializeWorkingEocEmailConfig() {
    this.hasUnsavedConfigData = false;
    if (this.eocEmailConfig) {
      Object.assign(this.workingEocEmailConfig, this.eocEmailConfig);
    }
  }

  getCycles() {
    this.cycleDropDownItems = [];
    this.cycleResource
      .getAllCycles(this.fiId)
      .then((res: Cycle[]) => {
        const today = new Date().getTime();
        const filteredCycles = res.filter((cycle: Cycle) => Date.parse(cycle.dropDate) <= today);
        filteredCycles.forEach((cycle: Cycle) => {
          this.cycleDropDownItems.push({
            dropDate: this.getFormattedDropDate(cycle.dropDate),
            id: cycle.id,
          });
        });
        this.selectedCycleDropDownItem = this.cycleDropDownItems[0];

        this.getActiveProducts();
        this.getEocEmailEvents();
        this.getEocEmailMetrics();
      });
  }

  getFormattedDropDate = (dropDate : string) => {
    const [year, month, day] = dropDate.split('-');
    return `Drop Date: ${month}/${day}/${year}`;
  }

  getActiveProducts() {
    this.activeProducts = [];
    this.productResource
      .getCurrentForConfigVersion(this.fiId)
      .then((res: Product[]) => {
        this.activeProducts = res.filter((product: Product) => product.active === true
        && product.productMapping !== null
        && product.productMapping.productType !== 'KASASA_SAVER');
        this.getEocEmailEnableFlagsOnLoad();
      });
  }

  getEocEmailEnableFlagsOnLoad() {
    this.activeProducts.forEach((activeProduct) => {
      this.eocEmailEnableFlagsOnLoad.set(activeProduct.id!, activeProduct.eocEmailEnabled);
    });
    this.hasUnsavedEnableEocEmailFlag = false;
  }

  getEocEmailEvents() {
    this.eocEmailEvents = [];
    this.eocEmailEventResource
      .get(this.fiId)
      .then((res: EocEmailEvent[]) => {
        this.eocEmailEvents = res;
        this.getSendHistoryForSelectedCycle();
      });
  }

  @Watch('selectedCycleDropDownItem')
  getSendHistoryForSelectedCycle() {
    this.sendHistoryForSelectedCycle = [];
    if (this.selectedCycleDropDownItem?.dropDate) {
      this.sendHistoryForSelectedCycle = this.eocEmailEvents
        .filter(event => this.getFormattedDropDate(event.cycleDropDate) === this.selectedCycleDropDownItem?.dropDate)
        .sort((event1, event2) => {
          if (event1.timestamp < event2.timestamp) {
            return 1;
          }
          return -1;
        });
    }
  }

  @Watch('selectedCycleDropDownItem')
  getEocEmailMetrics() {
    this.eocEmailMetricsForSelectedCycle = null;
    if (!this.selectedCycleDropDownItem || !this.selectedCycleDropDownItem?.id) {
      return;
    }
    this.isEmailCountLoading = true;
    this.eocEmailMetricsResource
      .get(this.fiId, this.selectedCycleDropDownItem?.id!)
      .then((res) => {
        this.eocEmailMetricsForSelectedCycle = res;
      })
      .finally(() => {
        this.isEmailCountLoading = false;
      });
  }

  onHomeUrlChange() {
    const existingUrl: string = (this.eocEmailConfig && this.eocEmailConfig.fiHomePageUrl) ? this.eocEmailConfig.fiHomePageUrl : '';
    const workingUrl: string = this.workingEocEmailConfig.fiHomePageUrl;
    const existingManualOverride: boolean = (this.eocEmailConfig && this.eocEmailConfig.fiHomePageUrlManualVerified) ? this.eocEmailConfig.fiHomePageUrlManualVerified : false;
    this.workingEocEmailConfig.fiHomePageUrlManualVerified = workingUrl === existingUrl
        && existingUrl !== '' && existingManualOverride === true;
  }

  getLatestProductSendTimestamp(product: Product) {
    if (this.selectedCycleDropDownItem?.dropDate) {
      const matches = this.eocEmailEvents
        .filter(event => event.externalProductMappingId === product.productMapping.externalProductMappingId
            && this.getFormattedDropDate(event.cycleDropDate)
                === this.selectedCycleDropDownItem?.dropDate)
        .sort((event1, event2) => {
          if (event1.timestamp < event2.timestamp) {
            return 1;
          }
          return -1;
        });
      if (matches.length === 0) {
        return 'Never Sent For Cycle';
      }
      return this.formatTimestamp(matches[0].timestamp);
    }
    return 'No Cycle Selected';
  }

  formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  }

  getEocEmailCount(product: Product) {
    if (this.eocEmailMetricsForSelectedCycle
        && this.eocEmailMetricsForSelectedCycle.externalProductMappingIdEmailCounts
        && (String(product.productMapping.externalProductMappingId)
          in this.eocEmailMetricsForSelectedCycle.externalProductMappingIdEmailCounts)) {
      return this.eocEmailMetricsForSelectedCycle.externalProductMappingIdEmailCounts[product.productMapping.externalProductMappingId!];
    }
    return 'N/A';
  }

  sendTestEmail() {
    if (!this.emailPattern.test(this.testEmailAddress as string)) {
      this.setError('Enter a valid Test Email Address!');
      return;
    }

    this.enableSendEmail = false;
    const testEmail: TestEmail = {
      testEmailAddress: this.testEmailAddress as string,
    };
    this.sendEocEmailResource.testEocEmail(this.fiId, this.selectedCycleDropDownItem?.id as number, testEmail)
      .then(() => {
        this.showSuccess('Sent test EOC email');
      })
      .catch((error) => {
        this.showError(error);
      });
  }

  sendEmailNow() {
    const dialog = new Dialog('Confirm',
      'Are you sure you want to send EOC emails to ALL accounts?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          this.enableSendEmail = false;
          this.sendEocEmailResource.sendEocEmail(this.fiId, this.selectedCycleDropDownItem?.id as number)
            .then(() => {
              this.loadAllData();
              this.showSuccess('Sent EOC email');
            })
            .catch((error) => {
              this.showError(error);
            });
        }
      });
  }

  onEocEmailEnableFlagModified() {
    this.hasUnsavedEnableEocEmailFlag = false;
    this.activeProducts.forEach((product) => {
      if (product.eocEmailEnabled !== this.eocEmailEnableFlagsOnLoad.get(product.id!)) {
        this.hasUnsavedEnableEocEmailFlag = true;
      }
    });
  }

  // this is temporary while we still are supporting ehlEnabled flag
  onHousingLogoChange() {
    this.workingEocEmailConfig.ehlEnabled = this.workingEocEmailConfig.housingLogo === 'EHL';
  }

  @Watch('workingEocEmailConfig', { deep: true })
  isConfigDataModified() {
    if (JSON.stringify(this.eocEmailConfig) !== JSON.stringify(this.workingEocEmailConfig)) {
      this.hasUnsavedConfigData = true;
    } else {
      this.hasUnsavedConfigData = false;
    }
  }

  @Watch('workingEocEmailConfig.emailProvider')
  onEmailProviderChange() {
    this.adjustBatchSizeOnEmailProviderChange();
    this.adjustFromSfmcEmailDomainOnEmailProviderChange();
  }

  @Watch('workingEocEmailConfig.batchSize')
  onBatchSizeChange() {
    this.hasInvalidBatchSize = !this.isBatchSizeValid();
  }

  validateConfigData() {
    if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
      return true;
    }
    return false;
  }

  saveEocEmailSettings() {
    if (!this.validateConfigData()) {
      this.setError('Please correct errors in the form!');
      return;
    }
    if (!this.emailPattern.test(this.workingEocEmailConfig.fiContactEmail)) {
      const dialog = new Dialog('Confirm', 'Contact Email is not a valid email, are you sure you want to save?');
      dialog.setAcceptLabel('OK').setDeclineLabel('Cancel');
      this.$store.dispatch('notices/add', dialog)
        .then((ok) => {
          if (ok === 'accept') {
            this.saving = true;
            this.adjustBatchSizeBeforeSave();
            Promise.allSettled([this.saveActiveProducts(), this.saveEocEmailConfig()])
              .then((results) => {
                this.saving = false;
              });
          }
        });
    } else {
      this.saving = true;
      Promise.allSettled([this.saveActiveProducts(), this.saveEocEmailConfig()])
        .then((results) => {
          this.saving = false;
        });
    }
  }

  saveEocEmailConfig() {
    return new Promise<void>((resolve, reject) => {
      if (this.hasUnsavedConfigData) {
        this.eocEmailConfigResource.update(this.fiId, this.workingEocEmailConfig)
          .then((res: EocEmailConfig) => {
            this.eocEmailConfig = res;
            this.initializeWorkingEocEmailConfig();
            this.invalidUrlError = false;
            resolve();
          })
          .catch((error) => {
            if (this.isInvalidURLError(error)) {
              this.invalidUrlError = true;
              this.workingEocEmailConfig.fiHomePageUrlManualVerified = false;
              this.setError(`Error, did not save configurations.  Invalid or unreachable homepage URL: ${this.workingEocEmailConfig.fiHomePageUrl}`);
            } else {
              this.showError(error);
            }
            reject();
          });
      } else {
        this.invalidUrlError = false;
        resolve();
      }
    });
  }

  saveActiveProducts() {
    const promises: Promise<void>[] = [];
    this.activeProducts.forEach((product) => {
      if (product.eocEmailEnabled !== this.eocEmailEnableFlagsOnLoad.get(product.id!)) {
        promises.push(new Promise<void>((resolve, reject) => {
          this.productResource.update(this.fiId, product)
            .then((returnedProduct) => {
              this.showSuccess(`Saved product: ${returnedProduct.id}`);
              resolve();
            })
            .catch((error) => {
              this.showError(error);
              reject();
            });
        }));
      }
    });

    return Promise.allSettled(promises).then(() => {
      this.getEocEmailEnableFlagsOnLoad();
    });
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else if (error.response && typeof error.response.data === 'string') {
      this.setError(error.response.data);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  showSuccess(message: string) {
    const al = new Alert(message, NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  isInvalidURLError = (error: any) : boolean => {
    if (typeof error.response.data !== 'string') {
      return false;
    }

    const asString : string = error.response.data;
    return asString.includes('ERR_INV_OR_UNREACHABLE_HOME_URL');
  }

  adjustBatchSizeBeforeSave() {
    if (this.workingEocEmailConfig.emailProvider === 'SPARKPOST') {
      this.workingEocEmailConfig.batchSize = 50;
    }
  }

  adjustBatchSizeOnEmailProviderChange() {
    if (this.saving || this.loading) {
      return;
    }

    if (this.workingEocEmailConfig.emailProvider === 'SALESFORCE_MARKETING_CLOUD') {
      this.workingEocEmailConfig.batchSize = 10;
    } else if (this.workingEocEmailConfig.emailProvider === 'SPARKPOST') {
      this.workingEocEmailConfig.batchSize = 50;
    }
  }

  adjustFromSfmcEmailDomainOnEmailProviderChange() {
    if (this.saving || this.loading) {
      return;
    }

    if (this.workingEocEmailConfig.emailProvider === 'SALESFORCE_MARKETING_CLOUD') {
      this.workingEocEmailConfig.fromSfmcEmailDomain = '@email.rewardlocally.com';
    }
  }

  isBatchSizeValid() : boolean {
    if (this.workingEocEmailConfig.emailProvider === 'SALESFORCE_MARKETING_CLOUD') {
      const validation = ValidationTool.requiredAndBetweenRuleFunction(this.workingEocEmailConfig.batchSize, 1, 100, '');
      const isValid = validation === true;
      return isValid === true;
    } if (this.workingEocEmailConfig.emailProvider === 'SPARKPOST') {
      const batchSize = this.workingEocEmailConfig.batchSize;
      return `${batchSize}` === '50';
    }
    return false;
  }
}


interface CycleDropDownItem {
    dropDate: string,
    id: number,
}
</script>

<style lang="scss">
.italic-small-span span{
  font-size: small;
  font-style: italic;
}
.small-span span{
  font-size: small
}
</style>
